import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../services/api';

function AdminUsers({ darkMode = true, currentUser = null }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [assignableBikes, setAssignableBikes] = useState([]);
  const [assignBikeSearch, setAssignBikeSearch] = useState('');
  const [assignBikePage, setAssignBikePage] = useState(1);
  const [assignBikePages, setAssignBikePages] = useState(1);
  const [assignBikeTotal, setAssignBikeTotal] = useState(0);
  const [loadingAssignBikes, setLoadingAssignBikes] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    role: 'technician'
  });
  const [editData, setEditData] = useState({
    username: '',
    email: '',
    firstname: '',
    surname: '',
    password: '',
    role: 'technician',
    is_active: true
  });
  const [assignData, setAssignData] = useState({
    userId: '',
    bikeId: ''
  });

  const createRoleOptions = useMemo(() => {
    if (currentUser?.role === 'super_admin') return ['technician', 'company_admin', 'admin'];
    if (currentUser?.role === 'company_admin') return ['technician'];
    return ['technician', 'company_admin', 'admin'];
  }, [currentUser?.role]);

  const getEditRoleOptions = () => {
    let baseOptions = ['technician', 'company_admin', 'admin'];
    if (currentUser?.role === 'super_admin') baseOptions = ['technician', 'company_admin', 'admin', 'super_admin'];
    if (currentUser?.role === 'company_admin') baseOptions = ['technician'];

    const currentRole = selectedUser?.role;
    if (currentRole && !baseOptions.includes(currentRole)) {
      return [...baseOptions, currentRole];
    }
    return baseOptions;
  };

  useEffect(() => {
    fetchData();
  }, [currentPage, pageSize]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showAssignModal) return;
    fetchAssignableBikes();
  }, [showAssignModal, assignBikePage, assignBikeSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!createRoleOptions.includes(formData.role)) {
      setFormData((prev) => ({ ...prev, role: createRoleOptions[0] || 'technician' }));
    }
  }, [createRoleOptions, formData.role]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const usersRes = await adminAPI.getAllUsers({
        page: currentPage,
        per_page: pageSize
      });
      setUsers(usersRes.data.users || []);
      setTotalUsers(usersRes.data.total || 0);
      setTotalPages(usersRes.data.pages || 1);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const flashSuccess = (message) => {
    setSuccess(message);
    setTimeout(() => setSuccess(''), 3000);
  };

  const fetchAssignableBikes = async () => {
    try {
      setLoadingAssignBikes(true);
      const response = await adminAPI.getAllBikes({
        page: assignBikePage,
        per_page: 20,
        q: assignBikeSearch.trim() || undefined,
        is_activated: false
      });
      setAssignableBikes(response.data.bikes || []);
      setAssignBikePages(response.data.pages || 1);
      setAssignBikeTotal(response.data.total || 0);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load available bikes');
    } finally {
      setLoadingAssignBikes(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminAPI.createUser(formData);
      setShowAddModal(false);
      setFormData({ username: '', email: '', password: '', role: createRoleOptions[0] || 'user' });
      flashSuccess('User created successfully');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setSubmitting(true);
    try {
      const payload = {
        username: editData.username,
        email: editData.email,
        firstname: editData.firstname,
        surname: editData.surname,
        role: editData.role,
        is_active: Boolean(editData.is_active),
      };
      if (editData.password?.trim()) {
        payload.password = editData.password.trim();
      }
      await adminAPI.updateUser(selectedUser.id, payload);
      setShowEditModal(false);
      setSelectedUser(null);
      setEditData({
        username: '',
        email: '',
        firstname: '',
        surname: '',
        password: '',
        role: 'technician',
        is_active: true
      });
      flashSuccess('User updated successfully');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignBike = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await adminAPI.assignBikeToUser(assignData.userId, assignData.bikeId);
      setShowAssignModal(false);
      setAssignData({ userId: '', bikeId: '' });
      flashSuccess('Bike assigned successfully');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to assign bike');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    const willActivate = !user.is_active;
    const confirmed = window.confirm(
      willActivate
        ? `Activate ${user.username}?`
        : `Deactivate ${user.username}?`
    );
    if (!confirmed) return;

    try {
      if (willActivate) {
        await adminAPI.activateUser(user.id);
      } else {
        await adminAPI.deactivateUser(user.id);
      }
      flashSuccess(willActivate ? 'User activated' : 'User deactivated');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async (user) => {
    const confirmed = window.confirm(`Delete user ${user.username}? This cannot be undone.`);
    if (!confirmed) return;
    try {
      await adminAPI.deleteUser(user.id);
      flashSuccess('User deleted successfully');
      await fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const openAssignModal = (user) => {
    setSelectedUser(user);
    setAssignData({ userId: user.id, bikeId: '' });
    setAssignBikeSearch('');
    setAssignBikePage(1);
    setShowAssignModal(true);
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditData({
      username: user.username || '',
      email: user.email || '',
      firstname: user.firstname || '',
      surname: user.surname || '',
      password: '',
      role: user.role || 'user',
      is_active: Boolean(user.is_active)
    });
    setShowEditModal(true);
  };

  if (loading) {
    return <div className="loading">Loading users...</div>;
  }

  return (
    <div className="admin-section">
      <div className="section-header">
        <h2>Team Management</h2>
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
          Create New User
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      <div className="card">
        <div className="admin-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Bikes</th>
                <th>Frame Default</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td data-label="Username">{user.username || 'N/A'}</td>
                  <td data-label="Email">{user.email || 'N/A'}</td>
                  <td data-label="Role">
                    <span className={`badge ${user.role === 'super_admin' ? 'badge-danger' : 'badge-primary'}`}>
                      {user.role}
                    </span>
                  </td>
                  <td data-label="Status">
                    <span className={`badge ${user.is_active ? 'badge-success' : 'badge-secondary'}`}>
                      {user.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td data-label="Bikes">{user.bike_count || 0}</td>
                  <td data-label="Frame Default">{user.is_vin_default ? 'Yes' : 'No'}</td>
                  <td data-label="Actions">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      <button onClick={() => openAssignModal(user)} className="btn btn-secondary btn-sm">
                        Assign Bike
                      </button>
                      <button onClick={() => openEditModal(user)} className="btn btn-secondary btn-sm">
                        Edit
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        className="btn btn-sm"
                        style={{
                          background: user.is_active ? 'rgba(245, 158, 11, 0.15)' : 'rgba(74, 140, 24, 0.2)',
                          color: user.is_active ? '#f59e0b' : '#4A8C18',
                          border: user.is_active ? '1px solid rgba(245, 158, 11, 0.4)' : '1px solid rgba(74, 140, 24, 0.45)'
                        }}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button onClick={() => handleDeleteUser(user)} className="btn btn-danger btn-sm">
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalUsers > 0 && (
          <div style={{
            padding: '10px',
            borderTop: '1px solid rgba(255,255,255,0.08)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
            flexWrap: 'wrap',
            fontSize: '12px',
            color: 'var(--color-gray)'
          }}>
            <div>
              Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalUsers)} of {totalUsers}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="users-page-size">Per page</label>
              <select
                id="users-page-size"
                value={pageSize}
                onChange={(e) => {
                  setCurrentPage(1);
                  setPageSize(Number(e.target.value));
                }}
                style={{ padding: '6px 8px', borderRadius: '8px', fontSize: '12px' }}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage <= 1}
              >
                Prev
              </button>
              <span>Page {currentPage}/{totalPages}</span>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage >= totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create New User</h2>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>x</button>
            </div>

            <form onSubmit={handleAdd}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  required
                >
                  {createRoleOptions.map((role) => (
                    <option key={role} value={role}>
                      {role.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Create User'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="close-btn" onClick={() => setShowEditModal(false)}>x</button>
            </div>

            <form onSubmit={handleEdit}>
              <div className="form-group">
                <label>Username *</label>
                <input
                  type="text"
                  value={editData.username}
                  onChange={(e) => setEditData({ ...editData, username: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editData.email}
                  onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div className="form-group">
                  <label>First name</label>
                  <input
                    type="text"
                    value={editData.firstname}
                    onChange={(e) => setEditData({ ...editData, firstname: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Surname</label>
                  <input
                    type="text"
                    value={editData.surname}
                    onChange={(e) => setEditData({ ...editData, surname: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Role *</label>
                <select
                  value={editData.role}
                  onChange={(e) => setEditData({ ...editData, role: e.target.value })}
                  required
                >
                  {getEditRoleOptions().map((role) => (
                    <option key={role} value={role}>
                      {role.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>New password (optional)</label>
                <input
                  type="password"
                  value={editData.password}
                  onChange={(e) => setEditData({ ...editData, password: e.target.value })}
                  placeholder="Leave blank to keep current password"
                />
              </div>

              <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  id="edit-user-active"
                  type="checkbox"
                  checked={Boolean(editData.is_active)}
                  onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })}
                />
                <label htmlFor="edit-user-active" style={{ margin: 0 }}>Active</label>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Save Changes'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssignModal && (
        <div className="modal-overlay" onClick={() => setShowAssignModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Bike to {selectedUser?.username}</h2>
              <button className="close-btn" onClick={() => setShowAssignModal(false)}>x</button>
            </div>

            <form onSubmit={handleAssignBike}>
              <div className="form-group">
                <label>Select Bike *</label>
                <p style={{ fontSize: '12px', color: darkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)', margin: '4px 0 8px 0' }}>
                  Only inactive bikes (not yet assigned to a registered user) can be assigned.
                </p>
                <input
                  type="text"
                  value={assignBikeSearch}
                  onChange={(e) => {
                    setAssignBikeSearch(e.target.value);
                    setAssignBikePage(1);
                  }}
                  placeholder="Search bikes by frame number, brand or model"
                  style={{ marginBottom: '8px' }}
                />
                <select
                  value={assignData.bikeId}
                  onChange={(e) => setAssignData({ ...assignData, bikeId: e.target.value })}
                  required
                  disabled={loadingAssignBikes}
                >
                  <option value="">Select a bike</option>
                  {assignableBikes.length === 0 ? (
                    <option disabled>No inactive bikes available</option>
                  ) : (
                    assignableBikes.map((bike) => (
                      <option key={bike.id} value={bike.id}>
                        {bike.vin} - {bike.brand} {bike.model} ({bike.year})
                      </option>
                    ))
                  )}
                </select>
                {assignBikeTotal > 0 && (
                  <div style={{
                    marginTop: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    fontSize: '12px',
                    color: darkMode ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.65)'
                  }}>
                    <span>
                      Showing {((assignBikePage - 1) * 20) + 1}-{Math.min(assignBikePage * 20, assignBikeTotal)} of {assignBikeTotal}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setAssignBikePage(Math.max(1, assignBikePage - 1))}
                        disabled={assignBikePage <= 1 || loadingAssignBikes}
                      >
                        Prev
                      </button>
                      <span>Page {assignBikePage}/{assignBikePages}</span>
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setAssignBikePage(Math.min(assignBikePages, assignBikePage + 1))}
                        disabled={assignBikePage >= assignBikePages || loadingAssignBikes}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : 'Assign Bike'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsers;
